import { motion } from 'motion/react';

interface NeuralNetworkProps {
  learningValue: number;
  isDark: boolean;
}

export default function NeuralNetwork({
  learningValue,
  isDark,
}: NeuralNetworkProps) {
  const intensity = Math.min(1, learningValue / 1000);
  const pulseDuration = 4 - intensity * 3;

  // Define 3 neurons in a cluster
  const neurons = [
    { x: 80, y: 50 },
    { x: 40, y: 110 },
    { x: 120, y: 110 },
  ];

  return (
    <div className="relative flex aspect-square w-full max-w-[300px] items-center justify-center">
      <svg viewBox="0 0 160 160" className="h-full w-full">
        {/* Inter-neuron connections */}
        <motion.path
          d="M 80 50 L 40 110 L 120 110 Z"
          fill="none"
          stroke={isDark ? '#6366f1' : '#4f46e5'}
          strokeWidth={1 + intensity * 2}
          strokeOpacity={0.2}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2 }}
        />

        {/* Firing signals between neurons */}
        {[
          { from: 0, to: 1 },
          { from: 1, to: 2 },
          { from: 2, to: 0 },
        ].map((link, i) => (
          <motion.circle
            key={`signal-${i}`}
            r={2 + intensity * 2}
            fill={isDark ? '#818cf8' : '#6366f1'}
            animate={{
              cx: [neurons[link.from].x, neurons[link.to].x],
              cy: [neurons[link.from].y, neurons[link.to].y],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: pulseDuration,
              repeat: Infinity,
              delay: i * (pulseDuration / 3),
              ease: 'linear',
            }}
          />
        ))}

        {/* Neurons */}
        {neurons.map((n, i) => (
          <g key={`neuron-${i}`}>
            <motion.circle
              cx={n.x}
              cy={n.y}
              r={10 + intensity * 5}
              fill={isDark ? '#1e293b' : '#f8fafc'}
              stroke={isDark ? '#6366f1' : '#4f46e5'}
              strokeWidth={2}
              animate={{
                scale: [1, 1.1, 1],
                boxShadow: [
                  `0 0 0px #6366f1`,
                  `0 0 ${20 * intensity}px #6366f1`,
                  `0 0 0px #6366f1`,
                ],
              }}
              transition={{
                duration: pulseDuration,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
            {/* Dendrites */}
            {[...Array(6)].map((_, j) => (
              <line
                key={`d-${i}-${j}`}
                x1={n.x}
                y1={n.y}
                x2={n.x + Math.cos((j * Math.PI) / 3) * (15 + intensity * 10)}
                y2={n.y + Math.sin((j * Math.PI) / 3) * (15 + intensity * 10)}
                stroke={isDark ? '#6366f1' : '#4f46e5'}
                strokeWidth={1}
                strokeOpacity={0.3}
              />
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
}
